import errorHandlers from '@/services/error-handlers';
import http from '@/services/http';



export default {
  namespaced: true,

  state: {
    areLoading: false,
    itemIdsBeingAddedToOrder: [],
    items: []
  },

  getters: {
    availableFavoriteItems(state, getters) {
      const items = getters.favoriteItems.items
        .filter(item => !item.currentlyUnavailable && item.allModifiersAvailable);

      return items;
    },

    // Note: Favorite Item model has limited model data, so to get complete model data, cross-join data with menu item data.
    favoriteItems(state, getters, rootState) {
      const items = [];

      state.items.forEach((favoriteItem) => {
        const computedItem = {
          ...favoriteItem,
          descriptionText: '',
          displayName: favoriteItem.itemDescription,
          favoriteItemId: favoriteItem.id,
          images: [],
          isLowInventory: false,
          isNewItem: false,
          price: favoriteItem.pricePerUnitAfterMods
        };

        favoriteItem.modifiers.forEach((modifier) => {
          if (modifier.modifiers && modifier.modifiers.length > 0) {
            computedItem.descriptionText += `${modifier.itemDescription}: `;

            modifier.modifiers.forEach((mod) => {
              if (mod.itemDescription) {
                computedItem.descriptionText += `${mod.itemDescription}, `;
              }
            });
          }

          else {
            computedItem.descriptionText += `${modifier.itemDescription}, `;
          }
        });

        computedItem.descriptionText = computedItem.descriptionText.replace(/,\s$/, '');

        /*
          NOTE: We'll try to grab images for this favorite item from
          our currently loaded menu. If there's no match, we just won't
          show an image for this favorite item on the favorites page.
        */

        const menuItem = rootState.menu.items.find(itm => itm.menuItemId === favoriteItem.menuItemId);

        if (menuItem) {
          computedItem.isLowInventory = !!menuItem.isLowInventory;
          computedItem.isNewItem = !!menuItem.isNewItem;
          computedItem.images = menuItem.images;
          computedItem.maxAllowed = menuItem.maxAllowed;
        }

        items.push(computedItem);
      });

      return {
        ...state,
        items
      };
    }
  },

  actions: {
    async addFavoriteItemToOrder({ commit, dispatch }, options) {
      commit('SET_FAVORITE_ADD_TO_ORDER_START', options.item.favoriteItemId);

      await dispatch('orders/addItem', {
        item: {
          favoriteItemId: options.item.favoriteItemId,
          maxAllowed: options.item.maxAllowed,
          menuItemId: options.item.menuItemId,
          quantity: 1,
          modifiers: options.item.modifiers,
          specialInstructions: options.item.specialInstructions
        }
      }, {
        root: true
      });

      commit('SET_FAVORITE_ADD_TO_ORDER_FINISH', options.item.favoriteItemId);
    },

    async favoriteItem({ commit, dispatch, rootGetters }, options) {
      try {
        const menu = rootGetters['menu/menu'];

        const response = await http.post('/cardfree-api/v1/account/me/favorite-items', {
          nickname: '',
          menuItemId: options.item.menuItemId,
          menuType: menu.menuType,
          modifiers: options.modifiers,
          specialInstructions: options.specialInstructions
        });

        commit('menu/SET_SELECTED_FAVORITE_ITEM', response.data.id, {
          root: true
        });

        dispatch('updateOrderItemConditionally', {
          isReceipt: options.isReceipt,
          item: options.item,
          favoriteItemId: response.data.id
        });

        if (options.disableStateUpdate) {
          commit('UPDATE_STALE_FAVORITE_ID', {
            item: options.item,
            newFavoriteId: response.data.id
          });
        }

        else {
          dispatch('getFavoriteItems', {
            silent: true
          });
        }
      }

      catch (error) {
        throw error;
      }
    },

    async getFavoriteItems({ commit, rootGetters }, options = {}) {
      const currentUser = rootGetters['session/currentUser'];
      const menu = rootGetters['menu/menu'];
      const selectedLocation = rootGetters['locations/selectedLocation'];

      if (!currentUser.accountId) {
        return;
      }

      try {
        if (!options.silent) {
          commit('SET_LOADING_STATUS', true);
        }

        const queryParams = {};

        if (selectedLocation.storeId) {
          queryParams.storeId = selectedLocation.storeId;
          queryParams['menu-type'] = menu.menuType;
        }

        const response = await http.get('/cardfree-api/v1/account/me/favorite-items', {
          params: queryParams
        });

        response.data.forEach((item) => {
          item.originalFavoriteItemId = item.id;
        });

        commit('SET_FAVORITE_ITEMS', response.data);
      }

      catch (error) {
        if (options.silentError) {
          errorHandlers.silent(error);
        }

        else {
          errorHandlers.generic(error);
        }
      }

      finally {
        commit('SET_LOADING_STATUS', false);
      }
    },

    pruneUnfavoritedItems({ commit }) {
      commit('PRUNE_UNFAVORITED_ITEMS');
    },

    async unfavoriteItem({ commit, dispatch, state }, options) {
      try {
        await http.delete(`/cardfree-api/v1/account/me/favorite-items/${options.item.favoriteItemId}`);

        commit('menu/SET_SELECTED_FAVORITE_ITEM', null, {
          root: true
        });

        dispatch('updateOrderItemConditionally', {
          isReceipt: options.isReceipt,
          item: options.item,
          favoriteItemId: null
        });

        if (options.disableStateUpdate) {
          commit('UPDATE_STALE_FAVORITE_ID', {
            item: options.item,
            newFavoriteId: null
          });
        }

        else {
          const items = state.items.filter(item => options.item.favoriteItemId !== item.id);

          commit('SET_FAVORITE_ITEMS', items);
        }
      }

      catch (error) {
        throw error;
      }
    },

    async updateOrderItemConditionally({ dispatch }, { isReceipt, ...options }) {
      if (isReceipt) await dispatch('updateOrderItemConditionallyForReceipt', options);
      else await dispatch('updateOrderItemConditionallyForActiveOrder', options);
    },

    async updateOrderItemConditionallyForActiveOrder({ commit, rootGetters }, { item, favoriteItemId }) {
      const order = rootGetters['orders/order'];

      const orderItemToUpdate = order.orderItems.find(orderItem => (item.orderItemId
        /* This occurs when we are in context of a specific order item (cart modal) */
        ? orderItem.orderItemId === item.orderItemId
        /* This occurs when we are outside of the context of a specific order item (menu list, menu item modal) */
        : orderItem.favoriteItemId === item.favoriteItemId));


      /**
       * There won't be an order item to update when updating the favorite status of an item
       * from outside the cart, unless we're "unfavoriting" a fav item currently in the cart.
       */
      if (orderItemToUpdate) {
        const { orderItemId } = orderItemToUpdate;

        await http.put(`/cardfree-api/v1/account/me/order/${order.orderId}/item/${orderItemId}/favorite`, {
          favoriteItemId
        });

        commit('orders/SET_ORDER', {
          ...order,
          orderItems: order.orderItems.map(orderItem => ({
            ...orderItem,
            ...orderItem.orderItemId === orderItemId && { favoriteItemId }
          }))
        }, { root: true });
      }
    },

    updateOrderItemConditionallyForReceipt({ commit }, options) {
      commit('receipt/UPDATE_ORDER_ITEM', {
        orderItemId: options.item.orderItemId,
        params: {
          favoriteItemId: options.favoriteItemId
        }
      }, {
        root: true
      });
    }
  },

  mutations: {
    PRUNE_UNFAVORITED_ITEMS(state) {
      const items = state.items.filter(item => !!item.id);

      state.items = items;
    },

    SET_FAVORITE_ADD_TO_ORDER_FINISH(state, idToFinish) {
      state.itemIdsBeingAddedToOrder = state.itemIdsBeingAddedToOrder.filter(id => id !== idToFinish);
    },

    SET_FAVORITE_ADD_TO_ORDER_START(state, idToAdd) {
      state.itemIdsBeingAddedToOrder.push(idToAdd);
    },

    SET_FAVORITE_ITEMS(state, items) {
      state.items = items || [];
    },

    SET_LOADING_STATUS(state, areLoading) {
      state.areLoading = areLoading;
    },

    UPDATE_STALE_FAVORITE_ID(state, options) {
      const item = state.items.find(itm => itm.originalFavoriteItemId === options.item.originalFavoriteItemId);

      if (item) {
        item.id = options.newFavoriteId;
      }
    }
  }
};
